import React, { useEffect, useMemo, useState } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowBack } from "@mui/icons-material";
import { Tooltip, Autocomplete, TextField } from "@mui/material";
import { Button, EditorInput } from "./ScheduleEditorUI";
import { getTimeZones } from "@vvo/tzdb";
import { failed, success } from "../../common/Toastify";
import { useSchedulingOwner } from "../../../hooks/ReactQueryHooks/useSchedulingOwner";
import { useSchedulingPermissions } from "./permissions";
import { useServiceCategoriesQuery } from "../../../hooks/ReactQueryHooks/useServiceCategoriesQuery";
import { useScheduleQuery, useSchedulesQuery } from "../../../hooks/ReactQueryHooks/useSchedulesQuery";
import { createSchedule, updateSchedule, cancelSchedule } from "../../api/Scheduling/schedules";
import { schedulingErrorMessage } from "../../api/Scheduling/errors";
import { exclusiveEndToInclusiveDate, inclusiveDateToExclusiveEnd, inclusiveDateToStart, instantToLocalDate } from "./time";
import { displayPractitionerName, loadPractitionerName, practitionerIdFromUser } from "./names";
import { scheduleConflictMessage } from "./ranges";
import { useConfirmDialog } from "../../../hooks/useConfirmDialog";
import WindowCategoryPicker from "./WindowCategoryPicker";
import EndDateChoice from "./EndDateChoice";
import "./Scheduling.css";

const schema = Yup.object({
  practitionerRef: Yup.string().required("Required field"),
  timezoneName: Yup.string().required("Required field"),
  startDate: Yup.string().required("Required field"),
  openEnded: Yup.boolean(),
  endDate: Yup.string().when("openEnded", {
    is: false,
    then: (field) =>
      field
        .required("Choose an end date.")
        .test("after-start", "End date must be on or after start date.", function (value) {
          const { startDate } = this.parent;
          return !value || !startDate || value >= startDate;
        }),
    otherwise: (field) => field.notRequired(),
  }),
  serviceCategoryIds: Yup.array().min(1, "Select at least one category"),
});

const ScheduleForm = ({ embedded = false, scheduleId: scheduleIdProp }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { getConfirmation } = useConfirmDialog();
  const { excludedOrganizationRefs, organizationRef, organizationTimeZone, practitionerRef, isSuperAdmin, user } = useSchedulingOwner();
  const { canCreateEditSchedule, practitioner } = useSchedulingPermissions();
  const selectedPractitioner = location.state?.data;
  const scheduleId = scheduleIdProp || location.state?.scheduleId || searchParams.get("scheduleId") || "";
  const isEdit = !!scheduleId;
  const [btnLoading, setBtnLoading] = useState(false);
  const [practitionerName, setPractitionerName] = useState("");
  const lockedPractitioner = practitioner && !isSuperAdmin && !selectedPractitioner;
  const resolvedPractitionerRef = selectedPractitioner
    ? practitionerIdFromUser(selectedPractitioner)
    : (lockedPractitioner ? practitionerRef : "");

  const { data: existing } = useScheduleQuery({ organizationRef, scheduleId, enabled: isEdit });
  const { data: categories = [] } = useServiceCategoriesQuery({ scope: "descendants", organizationRef, active: true, excludedOrganizationRefs, practitionerRef: existing?.practitionerRef || resolvedPractitionerRef });

  const timezoneOptions = useMemo(
    () =>
      getTimeZones().map((zone) => ({
        name: zone.name,
        label: zone.name,
        value: zone.name,
      })),
    []
  );

  const formik = useFormik({
    initialValues: {
      practitionerRef: resolvedPractitionerRef,
      timezoneName: organizationTimeZone || "America/St_Johns",
      startDate: "",
      openEnded: true,
      endDate: "",
      serviceCategoryIds: [],
      version: 1,
    },
    enableReinitialize: true,
    validationSchema: schema,
    onSubmit: (values) => {
      if (!isEdit && !resolvedPractitionerRef) {
        failed("This user has no practitioner record.");
        return;
      }
      const payload = {
        organizationRef,
        timezoneName: values.timezoneName,
        startsAt: inclusiveDateToStart(values.startDate, values.timezoneName),
        endsAt: values.openEnded ? null : inclusiveDateToExclusiveEnd(values.endDate, values.timezoneName),
        serviceCategoryIds: values.serviceCategoryIds,
      };
      setBtnLoading(true);
      const request = isEdit
        ? updateSchedule({
            ...payload,
            scheduleId,
            expectedVersion: values.version,
          })
        : createSchedule({ ...payload, practitionerRef: resolvedPractitionerRef });
      request
        .then((res) => {
          const nextId = res?.scheduleId || scheduleId || res?.schedule?.scheduleId;
          success(isEdit ? "Schedule updated" : "Schedule created");
          navigate(`/app/edit-schedule?scheduleId=${encodeURIComponent(nextId)}`, {
            state: {
              scheduleId: nextId,
              data: selectedPractitioner,
            },
            replace: true,
          });
        })
        .catch((error) => failed(schedulingErrorMessage(error)))
        .finally(() => setBtnLoading(false));
    },
  });

  useEffect(() => {
    if (!existing) return;
    formik.setValues({
      practitionerRef: existing.practitionerRef,
      timezoneName: existing.timezoneName,
      startDate: instantToLocalDate(existing.startsAt, existing.timezoneName),
      openEnded: !existing.endsAt,
      endDate: existing.endsAt ? exclusiveEndToInclusiveDate(existing.endsAt, existing.timezoneName) : "",
      serviceCategoryIds: existing.serviceCategoryIds || [],
      version: existing.version,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing]);

  useEffect(() => {
    const ref = formik.values.practitionerRef || resolvedPractitionerRef;
    if (!ref) {
      setPractitionerName("");
      return;
    }
    if (selectedPractitioner && practitionerIdFromUser(selectedPractitioner) === ref) {
      setPractitionerName(displayPractitionerName(selectedPractitioner));
      return;
    }
    if (ref === practitionerRef && user?.name) {
      const fromUser = displayPractitionerName(user);
      if (fromUser && fromUser !== "Unknown") {
        setPractitionerName(fromUser);
        return;
      }
    }
    let cancelledLoad = false;
    loadPractitionerName(ref).then((name) => {
      if (!cancelledLoad) setPractitionerName(name);
    });
    return () => {
      cancelledLoad = true;
    };
  }, [formik.values.practitionerRef, resolvedPractitionerRef, selectedPractitioner, practitionerRef, user]);

  // Guard against a second active schedule for the same practitioner. The API
  // rejects it too; this explains the conflict before the request is sent.
  const conflictPractitionerRef = formik.values.practitionerRef || resolvedPractitionerRef;
  const { data: practitionerSchedules = [] } = useSchedulesQuery({
    organizationRef,
    practitionerRef: conflictPractitionerRef,
    status: "active",
    enabled: !!organizationRef && !!conflictPractitionerRef,
  });
  const conflict = useMemo(() => {
    if (!formik.values.startDate) return null;
    return scheduleConflictMessage({
      schedules: practitionerSchedules,
      scheduleId,
      startsAt: inclusiveDateToStart(formik.values.startDate, formik.values.timezoneName),
      endsAt:
        formik.values.openEnded || !formik.values.endDate
          ? null
          : inclusiveDateToExclusiveEnd(formik.values.endDate, formik.values.timezoneName),
    });
  }, [
    practitionerSchedules,
    scheduleId,
    formik.values.startDate,
    formik.values.endDate,
    formik.values.openEnded,
    formik.values.timezoneName,
  ]);

  const cancelled = existing?.status === "cancelled";
  const categoryOptions = categories.map((category) => ({
    name: `${category.name} (${category.durationMinutes} min)`,
    value: category.serviceCategoryId,
          organizationRef: category.organizationRef, organizationName: category.organizationName || category.organizationRef,
  }));

  const handleCancelSchedule = async () => {
    const confirmed = await getConfirmation({
      title: "Attention!",
      entryName: "this schedule",
      actionBtnTitle: "Cancel schedule",
    });
    if (!confirmed) return;
    setBtnLoading(true);
    cancelSchedule({ organizationRef, scheduleId, expectedVersion: formik.values.version })
      .then(() => {
        success("Schedule cancelled");
        navigate("/app/schedule", { state: { data: selectedPractitioner, organizationRef } });
      })
      .catch((error) => failed(schedulingErrorMessage(error)))
      .finally(() => setBtnLoading(false));
  };

  if (!canCreateEditSchedule) {
    navigate("/app/dashboard");
    return null;
  }

  const formBody = (
        <form className="common-form border-fields" onSubmit={formik.handleSubmit}>
          <Row>
            <Col>
              <div className="field-wrap readonly">
                <label htmlFor="practitionerDisplay">
                  Practitioner <span style={{ color: "red" }}>*</span>
                </label>
                <EditorInput
                  id="practitionerDisplay"
                  disabled
                  value={practitionerName || "Loading..."}
                  readOnly
                />
              </div>
            </Col>
            <Col>
              <div className="field-wrap">
                <label htmlFor="schedule-timezone">Working-hours time zone <span style={{ color: "red" }}>*</span></label>
                <Autocomplete id="schedule-timezone" options={timezoneOptions} size="small"
                  value={timezoneOptions.find((opt) => opt.value === formik.values.timezoneName) || null}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  onChange={(_, selected) => formik.setFieldValue("timezoneName", selected?.value || "")}
                  disabled={cancelled} renderInput={(params) => <TextField {...params} required
                    placeholder="Select time zone" error={!!formik.touched.timezoneName && !!formik.errors.timezoneName}
                    helperText={formik.touched.timezoneName ? formik.errors.timezoneName : undefined} />} />
              </div>
            </Col>
          </Row>
          <div className="schedule-settings-dates">
            <div className="schedule-settings-start">
              <label htmlFor="startDate">
                Start date <span style={{ color: "red" }}>*</span>
              </label>
              <EditorInput
                id="startDate"
                type="date"
                value={formik.values.startDate}
                disabled={cancelled}
                onChange={(event) => formik.setFieldValue("startDate", event.target.value)}
              />
              {formik.errors.startDate ? <div className="error-text">{formik.errors.startDate}</div> : null}
            </div>
            <div>
              <EndDateChoice
                id="schedule-end"
                legend="End date"
                required
                openEnded={formik.values.openEnded}
                value={formik.values.endDate}
                min={formik.values.startDate}
                disabled={cancelled}
                error={formik.touched.endDate || formik.submitCount ? formik.errors.endDate : undefined}
                onBlur={() => formik.setFieldTouched("endDate", true)}
                onChange={({ openEnded, value }) => {
                  formik.setValues((values) => ({ ...values, openEnded, endDate: value }));
                }}
              />
            </div>
          </div>
          {conflict ? (
            <p className="schedule-conflict-notice" role="alert">
              {conflict}
            </p>
          ) : null}
          <Row className="mt-3">
            <Col>
              <WindowCategoryPicker
                label="Appointment types offered"
                options={categoryOptions}
                value={formik.values.serviceCategoryIds}
                disabled={cancelled}
                emptyHint="No active appointment types for this organization yet."
                onChange={(next) => formik.setFieldValue("serviceCategoryIds", next)}
              />
              {formik.errors.serviceCategoryIds ? (
                <div className="error-text">{formik.errors.serviceCategoryIds}</div>
              ) : null}
            </Col>
          </Row>
          <div className="btn-wrap scheduling-form-actions">
            {!embedded ? (
              <Button variant="secondary" onClick={() => navigate("/app/schedule", { state: { data: selectedPractitioner, organizationRef } })}>
                Cancel
              </Button>
            ) : null}
            {!cancelled ? (
              <Button type="submit" isLoading={btnLoading} disabled={!!conflict}>
                {isEdit ? "Save settings" : "Create schedule"}
              </Button>
            ) : (
              <span className="scheduling-status cancelled">Cancelled — create a new schedule</span>
            )}
            {isEdit && !cancelled ? (
              <Button
                variant="secondary"
                className="scheduling-cancel-schedule-btn"
                onClick={handleCancelSchedule}
                isLoading={btnLoading}
              >
                Cancel schedule
              </Button>
            ) : null}
          </div>
        </form>
  );

  if (embedded) {
    return formBody;
  }

  return (
    <section className="common-listing scheduling-workspace">
      <div className="heading-wrap mb-3">
        <Tooltip title="Go back">
          <div style={{ marginRight: "1rem", cursor: "pointer" }} onClick={() => navigate(-1)}>
            <ArrowBack />
          </div>
        </Tooltip>
      </div>
      <div className="custom-card p-4">{formBody}</div>
    </section>
  );
};

export default ScheduleForm;
